import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Route, Routes } from 'react-router-dom';
import Header from './Header';
import OfficeSpaceCard from './OfficeSpaceCard';
import ListingDetail from './ListingDetail';
import Login from './Login';
import WhyRoom2W from './WhyRoom2W';
import EarnWithUs from './EarnWithUs';
import UserProfile from './UserProfile';
import Modal from 'react-modal';
import Slider from '@mui/material/Slider';
import ProtectedRoute from './ProtectedRoute';
import AuthCallback from './AuthCallback';
import { useTranslation } from "react-i18next";
import "./i18n"; 
const AppContainer = styled.div`
  font-family: 'Inter', sans-serif;
`;

const OfficeSpaceSection = styled.section`
  padding: 20px;
  background-color: #f9f9f9;
`;

const CategoryBar = styled.div`
  display: flex;
  justify-content: right;
  margin: 20px 0;
`;

const CategoryButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  margin: 0 10px;
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }

  &.active {
    background-color: #0056b3;
  }
`;

const OfficeSpaceGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const LoadMoreButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  margin: 20px auto;
  display: block;
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

const FilterButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  margin: 0 10px;
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

const FilterLabel = styled.label`
  margin: 10px 0;
  display: block;
`;

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '300px',
    borderRadius: '10px',
    padding: '20px',
    backgroundColor: '#f9f9f9'
  }
};

const GlobalStyle = createGlobalStyle`
  body {
    background-color: #f9f9f9;
    margin: 0;
    padding: 0;
    font-family: 'Inter', sans-serif;
  }
`;

const App = () => {
  const [officeSpaces, setOfficeSpaces] = useState([]);
  const [visibleSpaces, setVisibleSpaces] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loadedCount, setLoadedCount] = useState(20);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    location: '',
    price: [0, 50],
    rating: [0, 5],
    occupants: [1, 20]
  });
  const [authenticated, setAuthenticated] = useState(false);
  const [language, setLanguage ] = useState('EN')

  const { i18n } = useTranslation();
  const { t } = useTranslation();

  // Fetch listings
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/listings?limit=20&offset=0`)
    // fetch('http://localhost:8000/listings?limit=20&offset=0')
      .then(response => response.json())
      .then(data => {
        setOfficeSpaces(data);
        setVisibleSpaces(data);
      })
      .catch(error => console.error('Error fetching office spaces:', error));
  }, []);

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setLoadedCount(20);
    // You can add category filtering logic here if applicable
  };

  // Load more spaces
  const loadMoreSpaces = () => {
  const newLoadedCount = loadedCount + 20;

  // Fetch more listings from the API with the updated offset
  fetch(`${process.env.REACT_APP_API_BASE_URL}/listings?limit=20&offset=${loadedCount}`)
  // fetch(`http://localhost:8000/listings?limit=20&offset=${loadedCount}`)
    .then(response => response.json())
    .then(data => {
      // Append the newly fetched data to the existing office spaces
      setOfficeSpaces(prevSpaces => [...prevSpaces, ...data]);
      setVisibleSpaces(prevSpaces => [...prevSpaces, ...data]);
      setLoadedCount(newLoadedCount);  // Update the loaded count
    })
    .catch(error => console.error('Error fetching more office spaces:', error));
};


  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  // Apply filters (calling the new API endpoint)
  const handleFilterApply = () => {
    const { price, rating, location, occupants } = filters;
    const minRating = rating[0];
    const maxPrice = price[1];
    const minOccupants = occupants[0];
    const maxOccupants = occupants[1];

    // Build query string for filtering
    let filterUrl = `${process.env.REACT_APP_API_BASE_URL}/listings/filter?limit=20&offset=0`;

    if (location) filterUrl += `&location=${location}`;
    if (minRating) filterUrl += `&min_rating=${minRating}`;
    if (maxPrice) filterUrl += `&max_price=${maxPrice}`;
    if (minOccupants) filterUrl += `&min_occupants=${minOccupants}&max_occupants=${maxOccupants}`;

    // Fetch filtered listings
    fetch(filterUrl)
      .then(response => response.json())
      .then(data => {
        setVisibleSpaces(data); // Update visible spaces
        setLoadedCount(20); // Reset the load count
        setFilterModalVisible(false); // Close the modal
      })
      .catch(error => console.error('Error fetching filtered office spaces:', error));
  };

  const categories = ['All', ...new Set(officeSpaces.map(space => space.category))];

  return (
    <AppContainer>
      <Header authenticated={authenticated} setAuthenticated={setAuthenticated} language={language} setLanguage={setLanguage}/>
      <Routes>
        <Route path="/" element={
          <>
            <CategoryBar>
              {/* {categories.map((category, index) => (
                <CategoryButton
                  key={index}
                  className={category === selectedCategory ? 'active' : ''}
                  onClick={() => handleCategorySelect(category)}
                >
                  {category}
                </CategoryButton>
              ))} */}
              <FilterButton onClick={() => setFilterModalVisible(true)}>
              {t('filters')}
              </FilterButton>
            </CategoryBar>
            <Modal
              isOpen={filterModalVisible}
              onRequestClose={() => setFilterModalVisible(false)}
              style={customStyles}
              contentLabel="Filter Modal"
            >
              <h2>{t('filterOptions')}</h2>
              <FilterLabel>
                {t('location')}
                <input
                  type="text"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                />
              </FilterLabel>
              <FilterLabel>
                {t('price')}: €{filters.price[0]} - €{filters.price[1]}
                <Slider
                  value={filters.price}
                  onChange={(e, value) => handleFilterChange('price', value)}
                  valueLabelDisplay="auto"
                  min={0}
                  max={50}
                />
              </FilterLabel>
              <FilterLabel>
                {t('rating')}: {filters.rating[0]} - {filters.rating[1]}
                <Slider
                  value={filters.rating}
                  onChange={(e, value) => handleFilterChange('rating', value)}
                  valueLabelDisplay="auto"
                  min={0}
                  max={5}
                  step={0.1}
                />
              </FilterLabel>
              <FilterLabel>
                {t('occupants')}: {filters.occupants[0]} - {filters.occupants[1]}
                <Slider
                  value={filters.occupants}
                  onChange={(e, value) => handleFilterChange('occupants', value)}
                  valueLabelDisplay="auto"
                  min={1}
                  max={20}
                />
              </FilterLabel>
              <FilterButton onClick={handleFilterApply}>{t('applyFilters')}</FilterButton>
            </Modal>
            <OfficeSpaceSection>
            <OfficeSpaceGrid>
              {visibleSpaces.length > 0 ? (
                visibleSpaces.map((space, index) => (
                  <OfficeSpaceCard
                    key={space.id}
                    space={space}
                    language={language}
                  />
                ))
              ) : (
                <p>{t('noListings')}</p> // Display message when no listings
              )}
            </OfficeSpaceGrid>
            {visibleSpaces.length >= 20 && (
              <LoadMoreButton onClick={loadMoreSpaces}>
                {t('loadMore')}
              </LoadMoreButton>
            )}
          </OfficeSpaceSection>
          </>
        } />
        <Route path="/login" element={<Login setAuthenticated={setAuthenticated} />} />
        <Route path="/why-room2w" element={<WhyRoom2W language={language} />} />
        <Route path="/earn-with-us" element={<EarnWithUs language={language} />} />
        <Route path="/listing/:id" element={
          <ListingDetail
            officeSpaces={visibleSpaces} // Pass visible spaces to detail page
            isAuthenticated={authenticated}
          />
        } />
        <Route path="/auth/callback" element={<AuthCallback setAuthenticated={setAuthenticated}/>} />
        <Route path="/reserve" element={
          <ProtectedRoute authenticated={authenticated}>
            <ListingDetail officeSpaces={visibleSpaces} />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute authenticated={authenticated}>
            <UserProfile />
          </ProtectedRoute>
        } />
      </Routes>
    </AppContainer>
  );
};

const WrappedApp = () => (
  <>
    <GlobalStyle />
    <App />
  </>
);

export default WrappedApp;
